import React, { useState, useEffect, useRef, useContext } from 'react'
import debounce from 'lodash.debounce'
import { motion, AnimatePresence } from 'framer-motion'
import { useCookies } from 'react-cookie'

import { GeolocalizationContext } from '../../../utils/contexts/geolocalization-context'
import useLockBodyScroll from '../../../utils/hooks/useLockBodyScroll'
import { getAgebyDate, validateDateField, validateFullDate } from '../../../utils/helpers/age-calculator'
import { lockAgeGate } from '../../../utils/helpers/age-lock'
import { getPageURL } from '../../../utils/helpers/page-url'
import triggerEventClick from '../../../utils/helpers/tracking/trigger-event-click'
import { ErrorDialog } from '../error-dialog'

/**
 * Display an age gating form to prevent underage users to access specific pages
 */

const AgeGateForm = ({ content, type, classes, dates, errors, dateFormat, lockCookieName, pageContext, eventSection }) => {
  const form = useRef(null)
  const { legalAge, currentLegalAge, ageGate, formSubmit } = useContext(GeolocalizationContext)
  const [countryLegalAge, setCountryLegalAge] = currentLegalAge
  const [ageGateSettings, setAgeGateSettings] = ageGate
  const [formSubmitted, setFormSubmitted] = formSubmit
  const [isLegalAge, setLegalAge] = legalAge
  const [cookies, setCookie] = useCookies()
  const [errorMessage, setErrorMessage] = useState(null)
  const cookieName = content.cookieName

  // Define the order that the fields will be displayed 
  // For localization purpose only, it won't have any impact on validation. 
  //Must be in the "dd-mm-yyyy" format.
  let date = dateFormat ? dateFormat.toUpperCase().split('-') : 'DD-MM-YYYY'
  if (date.length < 3) {
    date = 'DD-MM-YYYY'
  }

  // Lock <body> scroll when age gate is open
  if (type && (type === 'video' || type === 'modal')) {
    if (content.video && content.video.pageLock) {
      useLockBodyScroll(form.current)
    }
  } else {
    if (content.pageLock) {
      useLockBodyScroll(form.current)
    }
  }

  // Replace the legal age in the default age gate message
  const defaultErrorMessage = content.errorMessage
  const defaultErrorTitle = content.errorTitle
  const ageErrorTitle = defaultErrorTitle.replace('18', countryLegalAge)
  const ageErrorMessage = defaultErrorMessage.replace('18', countryLegalAge)

  // Fields ref
  const dayField = useRef(null)
  const monthField = useRef(null)
  const yearField = useRef(null)
  const submit = useRef(null)
  let dayInput = dayField.current
  let monthInput = monthField.current
  let yearInput = yearField.current

  useEffect(() => {
    dayInput = dayField.current
    monthInput = monthField.current
    yearInput = yearField.current

    const inputs = [dayInput, monthInput, yearInput]
    inputs.map((input) => (
      input.addEventListener('keypress', function (e) {
        if (isNaN(e.key)) {
          e.preventDefault()
        }
      })
    ))

  }, [])

  // Input Field Validataion
  const changeHandler = e => {
    const fieldMinLength = e.target.attributes.minLength ? parseInt(e.target.attributes.minLength.value) : ''
    const fieldLength = e.target.value.length

    // Make sure to have the minimum length of the field before validating it
    if (fieldMinLength && fieldLength < fieldMinLength) {
      return ''
    }

    const resultArray = validateDateField(e.target)
    const result = resultArray[0]
    const errorType = resultArray[1]
    if (result === false) {
      errorDisplay(errorType)
    } else {
      errorDisplay(null)
      dateValidation('silent')
    }
  }
  const debouncedChangeHandler = time => debounce(changeHandler, time)

  // Validate full date
  const dateValidation = (errorType) => {
    if (validateFullDate(dayInput, monthInput, yearInput, errorType) === true) {
      submit.current.classList.remove('disabled')
      submit.current.disabled = false
      submit.current.focus()
      return true
    } else {
      submit.current.disabled = true
      submit.current.classList.add('disabled')
      return false
    }
  }

  // Display Error
  const errorDisplay = (errorType) => {
    setErrorMessage(errorType)
  }

  // Submit form
  const submitHandler = e => {
    e.preventDefault()

    if (dateValidation() === true) {
      const date = yearInput.value + '/' + monthInput.value + '/' + dayInput.value
      if (date) {
        const resultArray = getAgebyDate(new Date(date), countryLegalAge)
        const resultBool = resultArray[0]
        const resultMessage = resultArray[1]
        if (resultMessage !== 'fakeDate') {
          if (resultBool !== false) {
            // Close popup 
            setLegalAge(true)
            setAgeGateSettings(prev => ({ ...prev, displayed: false, usage: null }))
            setFormSubmitted(true)

            // Track event in dataLayer
            if (pageContext?.dataLayer?.config === 'GA4') {
              let formDataLayer = {
                pageContext: pageContext,
                event: 'age_gate_success',
                page: 'age_gate',
                section: eventSection
              }
              triggerEventClick(formDataLayer)
            }

            // And add a cookie to keep it closed
            const currentDomain = `${getPageURL('domain')}`
            setCookie(cookieName, true, { domain: `${currentDomain}`, path: '/' })
          } else {
            // Lock-up the site for underage users
            if (ageGateSettings && ageGateSettings.locking) {
              lockAgeGate(ageGateSettings, lockCookieName)
              setLegalAge(false)
              setFormSubmitted(true)
            } else {
              // Display error
              errorDisplay('globalError')
            }
          }
        }
      }
    } else {
      errorDisplay('globalError')
    }
  }

  return (
    <div>
      <form
        ref={form}
        className={`age-gate-form ${classes && classes.form ? classes.form : 'box-border flex w-full flex-col text-white sm:h-auto sm:w-auto sm:justify-center'}`}
        onSubmit={submitHandler}
      >
        <div className="mb-10 lg:mb-0 flex w-full sm:w-auto space-x-4 font-bold">
          {date.map((input, index) => (
            <label
              key={index}
              htmlFor={`age-gate-${input}`}
              className="relative flex flex-col text-base"
            >
              {input === 'DD' ? dates.day : input === 'MM' ? dates.month : input === 'YYYY' ? dates.year : ''}
              <input
                ref={input === 'DD' ? dayField : input === 'MM' ? monthField : input === 'YYYY' ? yearField : null}
                id={`age-gate-${input}`}
                name={`age-gate-${input}`}
                type="number"
                pattern="[0-9]*"
                inputMode="numeric"
                onInput={(e) => e.target.value.length > input.length ? e.target.value = e.target.value.slice(0, input.length) : e.target.value}
                onChange={debouncedChangeHandler(200)}
                placeholder={dates[input]}
                minLength={input === 'YYYY' ? 4 : 1}
                maxLength={input.length}
                required=""
                index={index}
                tabIndex="0"
                className={`input ${classes && classes.input ? classes.input : 'mt-2 w-full text-center sm:w-36'}`}
                autoFocus={index === 0 && true}
              />
              <span className="input-focus"></span>
            </label>
          ))}
        </div>
        <button
          ref={submit}
          className={`age-gate-submit ${classes && classes.submit ? classes.submit : 'button button-primary-color button-narrow disabled h-20 w-full sm:w-1/4'}`}
          type="submit"
          tabIndex="0"
          onClick={submitHandler}
          disabled
        >
          <span>{content.buttonLabel}</span>
        </button>
      </form>
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            name={errorMessage}
            initial={{ opacity: 0, top: '-20px' }}
            animate={{ opacity: 1, top: 0 }}
            exit={{ opacity: 0, top: '-20px' }}
            transition={{
              duration: 0.5,
            }}
            className={`age-gate-error ${classes && classes.errorWrapper ? classes.errorWrapper : 'relative w-full flex justify-center'}`}
          >
            <ErrorDialog
              className={(classes && classes.error !== null) ? classes.error : ''}
              modalTitle={errorMessage === 'globalError' ? ageErrorTitle : errors && errors[errorMessage] ? errors[errorMessage] : 'Error'}
              modalMessage={errorMessage === 'globalError' ? ageErrorMessage : errors && errors[errorMessage + 'Desc'] ? errors[errorMessage + 'Desc'] : 'Please enter a valid date.'}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
export default AgeGateForm